/**
 * chart-data-service.ts
 * =====================
 * Module-level singleton holding the active dataset's CandleColumns.
 * Replaces the role that chart-source-service.snapshot.candles used to play
 * for storing the full 1M-row array.
 *
 * chart-runtime.ts reads from here to build windowed display slices.
 * replayStore.ts reads base columns for cursor/position resolution.
 */

import type { CandleColumns } from "@/lib/lytick/columnar";
import { aggregateColumns } from "@/lib/lytick/aggregate-columns";
import { parseTimeframeToMinutes } from "@/lib/lytick/replay-utils";

interface ActiveDataset {
  contentHash: string;
  symbol: string;
  granularitySeconds: number;
  base: CandleColumns;
  tiers: Map<number, CandleColumns>; // keyed by tfMinutes
}

let active: ActiveDataset | null = null;
let version = 0; // incremented on each loadDataset call

// ─── Write ────────────────────────────────────────────────────────────────────

export function loadDataset(
  contentHash: string,
  symbol: string,
  granularitySeconds: number,
  base: CandleColumns,
  tiers: Map<number, CandleColumns>,
): void {
  active = { contentHash, symbol, granularitySeconds, base, tiers };
  version += 1;
}

export function clearDataset(): void {
  active = null;
  version += 1;
}

// ─── Read ─────────────────────────────────────────────────────────────────────

export function getDatasetVersion(): number {
  return version;
}

export function getBaseColumns(): CandleColumns | null {
  return active?.base ?? null;
}

export function getGranularitySeconds(): number {
  return active?.granularitySeconds ?? 60;
}

export function hasDataset(): boolean {
  return active !== null && active.base.length > 0;
}

/**
 * Returns the best available CandleColumns for a given display timeframe.
 * - If TF ≤ base granularity: returns base columns.
 * - If a pre-built tier exists: returns it.
 * - Otherwise: computes and caches the tier on-demand from base (fallback for
 *   non-OPFS path where only the base was restored from Dexie).
 */
export function getDisplayColumns(tfMinutes: number): CandleColumns | null {
  if (!active) return null;

  const baseTfMinutes = Math.max(1, active.granularitySeconds / 60);

  if (tfMinutes <= baseTfMinutes) {
    return active.base;
  }

  const cached = active.tiers.get(tfMinutes);
  if (cached) return cached;

  // On-demand fallback: compute and cache (Dexie restore path)
  const computed = aggregateColumns(active.base, tfMinutes);
  active.tiers.set(tfMinutes, computed);
  return computed;
}

/**
 * Resolve the correct display CandleColumns for a timeframe string.
 * Returns base if the TF string can't be parsed or matches the base.
 */
export function getDisplayColumnsForTf(timeframe: string): CandleColumns | null {
  const tfMinutes = parseTimeframeToMinutes(timeframe);
  if (tfMinutes == null || !active) return active?.base ?? null;
  return getDisplayColumns(tfMinutes);
}
